import { useEffect, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';
import Tooltip from '@/shared-components/tooltip/Tooltip';

/**
 * Inline Save-Draft button for article editor.
 * - formState: the current form state, including post_title, etc.
 * - setFormState: setter for formState, used to attach the draft _id on first save.
 * - initialStateRef: ref to the initial formState for change detection.
 */
export default function SaveArticleDraft({
  formState,
  initialStateRef,
  loading,
  savePost
}) {
  const t = useTranslations('common.forms.board_editor');
  const [success, setSuccess] = useState(false);

  // 1) Has the user made any changes compared to the initial snapshot?
  const hasChanges = JSON.stringify(formState) !== JSON.stringify(initialStateRef.current);
  // 2) Must have a non-empty title to save
  const hasTitle = Boolean(formState.post_title?.trim());

  // Clear "Saved!" after 2 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // If they edit again, get rid of the "Saved!" message
  useEffect(() => {
    if (hasChanges && success) {
      setSuccess(false);
    }
  }, [hasChanges, success]);

  async function saveDraft() {
    // Only allow saving if:
    //  A) there’s a title,
    //  B) there are changes,
    //  C) we’re not currently loading.
    if (!hasTitle || !hasChanges || loading) return;
    const result = await savePost();
    if (result?.response?.ok) {
      setSuccess(true);
    }
  }

  const label = success ? t('saved') : t('save');

  // Disabled if no title, no changes, or currently saving
  const disabled = !hasTitle || !hasChanges || loading;
  const disabledTitle = !hasTitle ? t('title_required') : null;

  return (
    <Tooltip content={disabledTitle} placement="bottom" variant="error">
      <Text
        className={[`board-header__item save-draft
          ${disabled ? 'disabled' : ''}
          ${success ? 'read-only' : ''}`].filter(Boolean).join(" ")}
        onClick={disabled ? undefined : saveDraft}>
        {label}
        {loading && <Loader inline className="board-header__spinner" thin />}
      </Text>
    </Tooltip>
  );
}
